import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { VenueChoice } from '@/api/types';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';
import { NO_THEME, NO_THEME_LABEL, VENUES, venueOf } from './catalog';
import { VenueMark } from './VenueMark';

/** "No theme": a plain circle, crossed out. */
function NoThemeMark({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden className="shrink-0" data-venue-mark={NO_THEME}>
      <circle cx="20" cy="20" r="12" fill="none" stroke="currentColor" strokeWidth={2.5} opacity={0.55} />
      <path d="M11.5 28.5l17-17" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" opacity={0.55} />
    </svg>
  );
}

const NO_THEME_TILE = { id: NO_THEME, name: NO_THEME_LABEL, short: NO_THEME_LABEL, line: 'Just the two of you. No frame, no sound.' };

/**
 * Pick a venue (SPEC_V9 §B1): six flat tiles, each with its poster mark.
 * Used by the invite sheet, the planner's "Where are we going?" and a Date
 * Night's intro vote. `badges` marks other people's picks. v14: `allowNone`
 * adds a "No theme" tile first (a plain date).
 */
export function VenuePicker({
  value,
  onPick,
  size = 'md',
  disabled,
  pending,
  badges,
  testId = 'venue',
  label = 'Venue',
  allowNone = true,
}: {
  value: VenueChoice | null | undefined;
  onPick: (v: VenueChoice) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  /** The tile whose pick is being sent: shown picked and busy until it settles. */
  pending?: VenueChoice | null;
  badges?: Partial<Record<VenueChoice, ReactNode>>;
  testId?: string;
  label?: string;
  allowNone?: boolean;
}) {
  const reduce = useReducedMotion();
  const tiles = allowNone ? [NO_THEME_TILE, ...VENUES] : VENUES;
  return (
    <div role="radiogroup" aria-label={label} className={cn('grid gap-2', size === 'sm' ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3')} data-testid={`${testId}-picker`}>
      {tiles.map((v) => {
        const on = (pending ?? value) === v.id;
        return (
          <motion.button
            key={v.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={v.name}
            aria-busy={pending === v.id || undefined}
            disabled={disabled}
            whileTap={reduce || disabled ? undefined : { scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onClick={() => onPick(v.id)}
            className={cn(
              'relative flex flex-col rounded-card text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg disabled:opacity-60',
              size === 'sm' ? 'min-h-[84px] p-3' : size === 'lg' ? 'min-h-[140px] p-4' : 'min-h-[112px] p-3.5',
              on ? 'bg-primary text-ground' : 'bg-raised text-fg enabled:hover:bg-line',
            )}
            data-testid={`${testId}-${v.id}`}
          >
            {v.id === NO_THEME ? <NoThemeMark size={size === 'sm' ? 28 : 36} /> : <VenueMark venue={v.id} size={size === 'sm' ? 28 : 36} />}
            <span className={cn('mt-auto font-semibold leading-tight', size === 'sm' ? 'pt-2 text-[13px]' : 'pt-3 text-[15px]')}>{size === 'sm' ? v.short : v.name}</span>
            {size === 'lg' && <span className={cn('mt-1 text-[13px] leading-snug', on ? 'text-ground/75' : 'text-muted')}>{v.line}</span>}
            {on && (
              <span className={cn('absolute right-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-ground text-fg', badges?.[v.id] ? 'top-10' : 'top-2.5')} aria-hidden>
                <Icon name="check" size={14} strokeWidth={3} />
              </span>
            )}
            {badges?.[v.id] && <span className="absolute right-2 top-2">{badges[v.id]}</span>}
          </motion.button>
        );
      })}
    </div>
  );
}

/** A small venue chip (tickets, lobby, Friends → Dates). */
export function VenueChip({ venue, className, testId = 'venue-chip' }: { venue: VenueChoice | null | undefined; className?: string; testId?: string }) {
  const v = venueOf(venue);
  if (!v) return null;
  return (
    <span className={cn('inline-flex h-8 items-center gap-1.5 rounded-full bg-raised pl-1.5 pr-3 text-[13px] font-semibold text-fg', className)} data-testid={testId} data-venue={v.id}>
      <VenueMark venue={v.id} size={22} />
      {v.short}
    </span>
  );
}
